import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./PaymentSuccess.css";

/**
 * OrderSuccess component
 * - If `order` prop is provided it will be used (existing behavior).
 * - Otherwise it will read ?order_id=... from the URL and fetch order status from backend.
 * - Polls every 5s (for up to ~120s) until status becomes 'paid' or 'settled'.
 */
const OrderSuccess = ({ order }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // local order state: either from prop or fetched
  const [localOrder, setLocalOrder] = useState(order || null);
  const [loading, setLoading] = useState(!order); // if no order prop, we are loading
  const [error, setError] = useState(null);
  const [isFinal, setIsFinal] = useState(false); // whether payment is final (paid/settled)
  const pollingRef = useRef({ attempts: 0, timer: null });

  // get order id either from prop or query param
  const orderIdFromQuery = searchParams.get("order_id");
  const orderId = order?.id ?? orderIdFromQuery;

  // helper: check if a status should be considered final success
  const isSuccessStatus = (status) => {
    if (!status) return false;
    const s = String(status).toLowerCase();
    return s === "paid" || s === "settled" || s === "completed" || s === "success";
  };

  // fetch order status from backend
  const fetchOrderStatus = async (signal) => {
    if (!orderId) {
      setError("شناسه سفارش موجود نیست.");
      setLoading(false);
      return null;
    }

    setError(null);
    setLoading(true);
    try {
      const headers = { Accept: "application/json" };
      // if your API requires auth and you store token in localStorage:
      const token = localStorage.getItem("token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const apiBase = process.env.API_URL || "https://api.goharesadaf.ir";
      const res = await fetch(`${apiBase}/api/orders/${orderId}/status`, { headers, signal });
      if (!res.ok) {
        const text = await res.text().catch(() => null);
        throw new Error(text || `خطا در دریافت وضعیت سفارش (${res.status})`);
      }
      const data = await res.json();
      setLocalOrder(data);
      setIsFinal(isSuccessStatus(data.status));
      setLoading(false);
      return data;
    } catch (err) {
      if (err.name === "AbortError") return null;
      console.error("Fetch order status error:", err);
      setError(err.message || "خطا در دریافت وضعیت سفارش");
      setLoading(false);
      return null;
    }
  };

  // start polling until final or max attempts
  useEffect(() => {
    let controller = new AbortController();
    // if we already have an order prop and it's final, nothing to do
    if (order) {
      setIsFinal(isSuccessStatus(order.status));
      setLoading(false);
      return () => {};
    }

    // if no order id in query, just stop
    if (!orderId) {
      setLoading(false);
      setError("Order id not provided.");
      return () => {};
    }

    let mounted = true;
    const start = async () => {
      pollingRef.current.attempts = 0;
      // initial fetch
      const result = await fetchOrderStatus(controller.signal);
      if (!mounted) return;

      // if final, stop
      if (result && isSuccessStatus(result.status)) {
        setIsFinal(true);
        return;
      }

      // poll every 5s up to 24 attempts (~2 minutes)
      const maxAttempts = 24;
      const intervalMs = 5000;

      const tick = async () => {
        pollingRef.current.attempts += 1;
        if (pollingRef.current.attempts > maxAttempts) {
          // stop polling
          clearInterval(pollingRef.current.timer);
          pollingRef.current.timer = null;
          return;
        }
        const r = await fetchOrderStatus(controller.signal);
        if (r && isSuccessStatus(r.status)) {
          setIsFinal(true);
          if (pollingRef.current.timer) {
            clearInterval(pollingRef.current.timer);
            pollingRef.current.timer = null;
          }
        }
      };

      // schedule next calls
      pollingRef.current.timer = setInterval(tick, intervalMs);
    };

    start();

    return () => {
      mounted = false;
      controller.abort();
      if (pollingRef.current.timer) {
        clearInterval(pollingRef.current.timer);
        pollingRef.current.timer = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, order]); // re-run if order prop or query id changes

  // Manual refresh handler
  const handleRefresh = async () => {
    const controller = new AbortController();
    await fetchOrderStatus(controller.signal);
  };

  return (
    <div className="order-success-page">
      <div className="order-success-card">
        {/* Success illustration (unchanged) */}
        <div className="success-image">
          <img src="/images/paymentSuccess.png" alt="سفارش موفق" />
        </div>

        {/* Title: if final success show message, otherwise show waiting message */}
        <h1>
          {isFinal ? "سفارش شما با موفقیت ثبت شد!" : "در حال بررسی وضعیت پرداخت..." }
        </h1>

        {/* Order details */}
        <div className="order-details">
          {loading && !localOrder && <p>در حال بارگذاری اطلاعات سفارش...</p>}
          {error && <p className="error-text">خطا: {error}</p>}

          {localOrder ? (
            <>
              <p><strong>شماره سفارش:</strong> {localOrder?.order_id ?? localOrder?.id.toLocaleString("fa-IR") ?? Number(orderId).toLocaleString("fa-IR")}</p>
              <p>
                <strong>مبلغ کل:</strong>{" "}
                {Number(localOrder?.total ?? localOrder?.amount ?? 0).toLocaleString("fa-IR")} تومان
              </p>
              <p>
                <strong>تاریخ:</strong>{" "}
                {localOrder?.created_at ? new Date(localOrder.created_at).toLocaleDateString("fa-IR") : ""}
              </p>
              <p>
                <strong>وضعیت پرداخت:</strong>{" "}
                <span>{localOrder?.status ?? "—"}</span>
              </p>
              {localOrder?.transaction_id && (
                <p><strong>شمارۀ تراکنش:</strong> {localOrder.transaction_id}</p>
              )}
            </>
          ) : (
            !loading && <p>اطلاعات سفارش در دسترس نیست.</p>
          )}
        </div>

        {/* Action buttons */}
        <div className="order-actions">
          <button className="btn primary" onClick={() => navigate("/")}>
            بازگشت به صفحه اصلی
          </button>

          <button className="btn secondary" onClick={() => navigate("/orders")}>
            مشاهده سفارش‌های من
          </button>

          <button className="btn outline" onClick={() => navigate("/shop")}>
            ادامه خرید
          </button>

          {/* refresh and a "view order" link if order id exists */}
          <button className="btn small" onClick={handleRefresh} title="به‌روزرسانی وضعیت">
            {loading ? "در حال به‌روزرسانی..." : "به‌روزرسانی وضعیت"}
          </button>
          {orderId && (
            <button className="btn small outline" onClick={() => navigate(`/orders/${orderId}`)}>
              مشاهده سفارش
            </button>
          )}
        </div>

        {/* simple status note when waiting */}
        {!isFinal && !error && (
          <div style={{ marginTop: 12, color: "#666", fontSize: 13 }}>
            در صورتی که وضعیت پرداخت بلافاصله به‌روزرسانی نشد، چند لحظه صبر کنید و سپس روی «به‌روزرسانی وضعیت» کلیک کنید.
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderSuccess;
